import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { FileText, Highlighter, ZoomIn, ZoomOut } from 'lucide-react'
import { apiErrorText } from '@/lib/api-error'
import { findeMarken, type Fundstelle, type Marke, type Textstueck } from './fundstellen'

export interface OriginalDatei {
  daten: ArrayBuffer
  mimeType: string
}

/**
 * Wohin die Ansicht springen soll: zur Markierung einer Fundstelle, sonst zur Seite. `n` zählt hoch —
 * so springt auch der zweite Klick auf dieselbe Zeile.
 */
export interface OriginalZiel {
  id?: string | null
  seite?: number | null
  n: number
}

interface PdfSeite {
  getViewport: (o: { scale: number }) => { width: number; height: number; transform: number[] }
  render: (o: object) => { promise: Promise<void>; cancel: () => void }
  getTextContent: () => Promise<{ items: { str?: string; transform?: number[]; width?: number }[] }>
}
interface PdfDokument {
  getPage: (n: number) => Promise<PdfSeite>
  numPages: number
}
interface Seitentext {
  breite: number
  hoehe: number
  stuecke: Textstueck[]
  zeichen: number
}

/** Ohne Seitenangabe wird auf so vielen Seiten gesucht — wie der Server die Textebene liest (`MAX_SEITEN_TEXT`). */
const MAX_SEITEN_SUCHE = 30
const prozent = (anteil: number) => `${anteil * 100}%`

/**
 * AP-34 6., Nachtrag 2 5.5 — **das Original neben der Erfassung.** PDFs rendert pdf.js im
 * Browser (nicht die PDF-Anzeige des Browsers in einem Rahmen: Die ist am Telefon unzuverlässig,
 * und dort wird freigegeben). Bilder als Bild.
 *
 * Seit 13.10.2026 (AP-39 Nachtrag 1) eine Stelle für Eingangsrechnung und Posteingang — die Maske sagt,
 * woher die Datei kommt (`lade`). **Fundstellen:** Was gelesen wurde, wird auf der Seite markiert, wenn die
 * PDF eine Textebene hat (`fundstellen.ts`); `ziel` springt zur Markierung einer Zeile, sonst zu ihrer Seite.
 */
export function OriginalAnzeige({
  schluessel,
  lade,
  vorhanden,
  ziel,
  fundstellen,
  onFundstelle,
  titel = 'Original',
  leer,
}: {
  /** Kennung der Datei — geladen wird neu, wenn sie wechselt */
  schluessel: string
  lade: () => Promise<OriginalDatei>
  vorhanden: boolean
  ziel?: OriginalZiel | null
  fundstellen?: readonly Fundstelle[]
  /** Klick auf eine Markierung — die Maske zeigt die Zeile dazu */
  onFundstelle?: (id: string) => void
  titel?: string
  /** Was dasteht, wenn es keine Datei gibt */
  leer?: ReactNode
}) {
  const huelle = useRef<HTMLDivElement>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laedt, setLaedt] = useState(vorhanden)
  const [bild, setBild] = useState<string | null>(null)
  const [text, setText] = useState<string | null>(null)
  const [seiten, setSeiten] = useState(0)
  const [zoom, setZoom] = useState(1)
  // Die Breite der Ansicht ist verschiebbar: Die Seiten werden neu gezeichnet, sobald sie zur Ruhe kommt.
  const [breite, setBreite] = useState(0)
  const dokument = useRef<PdfDokument | null>(null)
  const texte = useRef(new Map<number, Seitentext>())
  const [marken, setMarken] = useState<Record<number, { breite: number; hoehe: number; marken: Marke[] }>>({})
  const [markieren, setMarkieren] = useState(true)
  // `null` = noch nicht gesucht; `true` = die durchsuchten Seiten haben keinen Text (Scan)
  const [ohneText, setOhneText] = useState<boolean | null>(null)
  const ladeJetzt = useRef(lade)
  ladeJetzt.current = lade

  useEffect(() => {
    if (!vorhanden) return
    let aus = false
    let adresse: string | null = null
    setLaedt(true)
    setFehler(null)
    setBild(null)
    setText(null)
    setSeiten(0)
    setMarken({})
    setOhneText(null)
    texte.current = new Map()
    void (async () => {
      try {
        const { daten, mimeType } = await ladeJetzt.current()
        if (aus) return
        if (mimeType.startsWith('image/')) {
          adresse = URL.createObjectURL(new Blob([daten], { type: mimeType }))
          setBild(adresse)
        } else if (mimeType === 'application/pdf') {
          const [pdfjs, worker] = await Promise.all([import('pdfjs-dist'), import('pdfjs-dist/build/pdf.worker.min.mjs?url')])
          pdfjs.GlobalWorkerOptions.workerSrc = worker.default
          const doc = await pdfjs.getDocument({ data: new Uint8Array(daten), isEvalSupported: false }).promise
          if (aus) return
          dokument.current = doc as unknown as PdfDokument
          setSeiten(doc.numPages)
        } else {
          // E-Rechnung als XML: bis zur Sichtdarstellung (Phase I) der Text
          setText(new TextDecoder('utf-8').decode(daten).slice(0, 200_000))
        }
      } catch (e) {
        if (!aus) setFehler(apiErrorText(e, 'Das Original ließ sich nicht laden.'))
      } finally {
        if (!aus) setLaedt(false)
      }
    })()
    return () => {
      aus = true
      if (adresse) URL.revokeObjectURL(adresse)
      dokument.current = null
    }
  }, [schluessel, vorhanden])

  useEffect(() => {
    const el = huelle.current
    if (!el) return
    let warten: ReturnType<typeof setTimeout> | undefined
    const ro = new ResizeObserver(([e]) => {
      clearTimeout(warten)
      warten = setTimeout(() => setBreite(Math.round(e.contentRect.width)), 150)
    })
    ro.observe(el)
    return () => {
      clearTimeout(warten)
      ro.disconnect()
    }
  }, [vorhanden])

  // Seiten zeichnen — neu bei anderer Seitenzahl, Vergrößerung oder Breite der Ansicht
  useEffect(() => {
    const doc = dokument.current
    const el = huelle.current
    // `breite` kommt vom Beobachter, sobald die Ansicht ihre Größe hat — vorher gäbe es zwei Durchgänge.
    if (!doc || !el || !seiten || !breite) return
    let aus = false
    // Ein Durchgang, der von einer neuen Breite überholt wird, bricht ab: pdf.js zeichnet je Fläche nur einmal zugleich.
    let laufend: { promise: Promise<void>; cancel: () => void } | null = null
    void (async () => {
      const breite = Math.max(320, el.clientWidth - 24)
      for (let n = 1; n <= seiten; n++) {
        const page = await doc.getPage(n)
        if (aus) return
        const basis = page.getViewport({ scale: 1 })
        const scale = (breite / basis.width) * zoom
        const vp = page.getViewport({ scale: scale * (window.devicePixelRatio || 1) })
        const c = el.querySelector<HTMLCanvasElement>(`canvas[data-seite="${n}"]`)
        if (!c) continue
        c.width = vp.width
        c.height = vp.height
        c.style.width = `${basis.width * scale}px`
        c.style.height = `${basis.height * scale}px`
        const ctx = c.getContext('2d')
        if (!ctx) continue
        laufend = page.render({ canvasContext: ctx, viewport: vp })
        try {
          await laufend.promise
        } catch {
          // abgebrochen — der nächste Durchgang zeichnet
          return
        }
      }
    })()
    return () => {
      aus = true
      laufend?.cancel()
    }
  }, [seiten, zoom, breite])

  // Fundstellen suchen — in der Textebene der Seiten, die genannt sind; ohne Seitenangabe in allen (bis zur Grenze).
  // Der Inhalt zählt, nicht das Feld: Die Maske baut die Liste bei jedem Zeichnen neu.
  const gesucht = useMemo(() => JSON.stringify(fundstellen ?? []), [fundstellen])
  useEffect(() => {
    const doc = dokument.current
    const liste = JSON.parse(gesucht) as Fundstelle[]
    if (!doc || !seiten || !markieren || !liste.length) {
      setMarken({})
      return
    }
    let aus = false
    void (async () => {
      try {
        const { Util } = await import('pdfjs-dist')
        const ueberall = liste.some((f) => !f.seite)
        const welche = ueberall
          ? Array.from({ length: Math.min(seiten, MAX_SEITEN_SUCHE) }, (_, i) => i + 1)
          : [...new Set(liste.map((f) => f.seite!))].filter((n) => n >= 1 && n <= seiten).sort((a, b) => a - b)
        const neu: typeof marken = {}
        let zeichen = 0
        for (const n of welche) {
          let s = texte.current.get(n)
          if (!s) {
            const page = await doc.getPage(n)
            const vp = page.getViewport({ scale: 1 })
            const inhalt = await page.getTextContent()
            const stuecke: Textstueck[] = []
            for (const it of inhalt.items) {
              if (!it.str?.trim() || !it.transform) continue
              // Grundlinie des Stücks in den Einheiten der Seite, Ursprung oben links — die Drehung der Seite steckt im Viewport.
              const t = Util.transform(vp.transform, it.transform) as number[]
              const hoehe = Math.hypot(t[2], t[3])
              stuecke.push({ text: it.str, x: t[4], y: t[5] - hoehe, w: it.width ?? 0, h: hoehe })
            }
            s = { breite: vp.width, hoehe: vp.height, stuecke, zeichen: stuecke.reduce((z, x) => z + x.text.length, 0) }
            texte.current.set(n, s)
          }
          if (aus) return
          zeichen += s.zeichen
          const gefunden = findeMarken(
            s.stuecke,
            liste.filter((f) => !f.seite || f.seite === n),
          )
          if (gefunden.length) neu[n] = { breite: s.breite, hoehe: s.hoehe, marken: gefunden }
        }
        if (aus) return
        setMarken(neu)
        setOhneText(welche.length > 0 && zeichen < 20)
      } catch {
        // Die Markierung ist eine Hilfe — scheitert sie, bleibt das Original lesbar.
        if (!aus) setMarken({})
      }
    })()
    return () => {
      aus = true
    }
  }, [seiten, gesucht, markieren])

  // Zum Ziel springen: zur Markierung der Zeile, sonst zu ihrer Seite. Kommt die Markierung erst nach dem
  // Klick (die Seite wurde noch gelesen), wird einmal nachgezogen — danach bleibt die Ansicht, wo sie ist.
  const gesprungen = useRef<{ n: number; zurMarke: boolean } | null>(null)
  useEffect(() => {
    const el = huelle.current
    if (!ziel || !seiten || !el) return
    const schon = gesprungen.current?.n === ziel.n ? gesprungen.current : null
    if (schon?.zurMarke) return
    const marke = ziel.id ? Array.from(el.querySelectorAll('[data-marke]')).find((m) => m.getAttribute('data-marke') === ziel.id) : undefined
    if (marke) {
      marke.scrollIntoView({ behavior: 'smooth', block: 'center' })
      gesprungen.current = { n: ziel.n, zurMarke: true }
    } else if (!schon) {
      if (ziel.seite) el.querySelector(`canvas[data-seite="${ziel.seite}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      gesprungen.current = { n: ziel.n, zurMarke: false }
    }
  }, [ziel, seiten, marken])

  if (!vorhanden) {
    return (
      <div className="flex h-full min-h-[12rem] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
        <FileText className="h-8 w-8 opacity-40" />
        {leer ?? 'Dazu ist keine Datei hinterlegt.'}
      </div>
    )
  }

  const stellen = fundstellen?.length ?? 0
  const markiert = new Set(Object.values(marken).flatMap((s) => s.marken.map((m) => m.id))).size
  return (
    <div className="flex h-full min-h-[24rem] flex-col overflow-hidden rounded-lg border border-border bg-muted/30">
      <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-1.5 text-xs text-muted-foreground">
        <span className="min-w-0 truncate">
          {titel}
          {seiten ? ` · ${seiten} ${seiten === 1 ? 'Seite' : 'Seiten'}` : ''}
        </span>
        <span className="flex shrink-0 items-center gap-1">
          {/* Fundstellen gibt es nur in einer PDF mit Textebene — ein Scan oder Foto sagt nicht, wo etwas steht. */}
          {stellen > 0 && (seiten > 0 || bild) && (
            <>
              {markieren && seiten > 0 && ohneText === false && (
                <span className="tabular-nums" title="So viele der gelesenen Werte stehen wörtlich im Text der Seite">
                  {markiert} von {stellen} markiert
                </span>
              )}
              {(bild || ohneText) && <span title="Ein Scan oder Foto hat keinen Text, in dem sich die Stelle finden ließe">Scan — ohne Markierungen</span>}
              {seiten > 0 && !ohneText && (
                <button
                  type="button"
                  className={`rounded p-1 hover:bg-muted ${markieren ? 'text-violet-700 dark:text-violet-300' : ''}`}
                  aria-pressed={markieren}
                  title={markieren ? 'Markierungen ausblenden' : 'Markieren, wo die gelesenen Werte stehen'}
                  aria-label="Fundstellen markieren"
                  onClick={() => setMarkieren((m) => !m)}
                >
                  <Highlighter size={14} />
                </button>
              )}
            </>
          )}
          {(seiten > 0 || bild) && (
            <>
              <button type="button" className="rounded p-1 hover:bg-muted" title="Verkleinern" aria-label="Verkleinern" onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))}>
                <ZoomOut size={14} />
              </button>
              <span className="w-10 text-center tabular-nums">{Math.round(zoom * 100)} %</span>
              <button type="button" className="rounded p-1 hover:bg-muted" title="Vergrößern" aria-label="Vergrößern" onClick={() => setZoom((z) => Math.min(3, z + 0.25))}>
                <ZoomIn size={14} />
              </button>
            </>
          )}
        </span>
      </div>
      <div ref={huelle} className="min-h-0 flex-1 overflow-auto p-3">
        {laedt && <p className="py-8 text-center text-sm text-muted-foreground">Original wird geladen …</p>}
        {fehler && <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{fehler}</div>}
        {bild && <img src={bild} alt={titel} style={{ width: `${zoom * 100}%`, maxWidth: 'none' }} className="mx-auto block rounded shadow" />}
        {text && <pre className="whitespace-pre-wrap break-all rounded bg-background p-3 text-xs text-foreground">{text}</pre>}
        {Array.from({ length: seiten }, (_, i) => {
          const blatt = marken[i + 1]
          return (
            // Das Blatt ist so groß wie seine Zeichenfläche — die Markierungen liegen in Anteilen darüber und wachsen mit.
            <div key={i + 1} className="relative mx-auto mb-3 w-fit">
              <canvas data-seite={i + 1} className="block rounded bg-white shadow" />
              {blatt?.marken.map((m, j) => (
                <button
                  key={j}
                  type="button"
                  tabIndex={-1}
                  data-marke={m.id}
                  title={m.titel}
                  aria-label={m.titel ?? 'Fundstelle'}
                  onClick={() => onFundstelle?.(m.id)}
                  className={`absolute rounded-sm mix-blend-multiply ${m.id === ziel?.id ? 'bg-violet-500/45 outline outline-2 outline-violet-600' : 'bg-violet-400/25'} ${onFundstelle ? 'cursor-pointer hover:bg-violet-500/40' : 'pointer-events-none'}`}
                  style={{ left: prozent((m.x - 2) / blatt.breite), top: prozent((m.y - 2) / blatt.hoehe), width: prozent((m.w + 4) / blatt.breite), height: prozent((m.h + 4) / blatt.hoehe) }}
                />
              ))}
            </div>
          )
        })}
      </div>
    </div>
  )
}
